import { AssetClassesQuery, EditInvestorPageGroupByPrimaryEntityIdQuery, EditInvestorPageLegalEntityQuery } from '@/__generated__/graphql';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import { Category, Eligibility, EntityType } from '@trace-8/trace8-schema';
import { InvestorDetailsEntityTypeEnum } from '../investorDetailsPage/types';
import {
  EditInvestorAllocationSettingsAlwaysShowOptionEnum,
  EditInvestorAllocationSettingsBiteSizeOptionEnum,
  EditInvestorAllocationSettingsOnHoldOptionEnum,
  EditInvestorAllocationTargetsAssetClassTargetsOptionEnum,
  EditInvestorAllocationTargetsCapacityCalculationOptionEnum,
  EditInvestorEntityDetailsTaxStatusOptionEnum,
  EditInvestorModalPageTypeEnum,
  EditInvestorSectionNameEnum,
  IEditInvestorFormData,
  IEditInvestorSectionsData
} from './types';

const getDecimalPart = (number: number): number => {
  const decimalPart = parseFloat((number % 1).toFixed(2).slice(2));
  return decimalPart !== 0 ? decimalPart : 0;
};

export const buildEditInvestorSectionsData = ({
  legalEntityData,
  groupData,
  assetClasses,
  targetSubAssetClasses,
  entityType
}: {
  legalEntityData?: EditInvestorPageLegalEntityQuery['legalEntity'];
  groupData?: EditInvestorPageGroupByPrimaryEntityIdQuery['group'];
  assetClasses: AssetClassesQuery['assetClasses'];
  targetSubAssetClasses: boolean;
  entityType: InvestorDetailsEntityTypeEnum;
}): IEditInvestorSectionsData[] => {
  const {
    ignoreAllocationTargets,
    ignoreBiteSize,
    sectorsToAvoid,
    onHold,
    custodied,
    unmanaged,
    alwaysShow,
    defaultBiteSize1,
    defaultBiteSize2,
    targetOCRatio,
    totalEntityValue,
    allocationTargets
  } = legalEntityData || groupData || {};

  const taxStatus = legalEntityData?.taxStatus
    ? EditInvestorEntityDetailsTaxStatusOptionEnum[legalEntityData?.taxStatus]
    : legalEntityData?.taxExempt
      ? EditInvestorEntityDetailsTaxStatusOptionEnum.US_TAX_EXEMPT
      : EditInvestorEntityDetailsTaxStatusOptionEnum.US_TAXABLE;

  return [
    {
      title: 'Entity Details',
      name: EditInvestorSectionNameEnum.EntityDetails,
      modalPageType: EditInvestorModalPageTypeEnum.EntityDetails,
      formData: {
        name: legalEntityData?.entityName || '',
        category: legalEntityData?.category || '',
        entityType: legalEntityData?.entityType || '',
        otherType: legalEntityData?.otherType || '',
        eligibility: legalEntityData?.eligibility || [],
        aboutInvestorNote: legalEntityData?.aboutInvestorNote || '',
        entityInception: legalEntityData?.entityInception || '',
        taxStatus
      } as IEditInvestorFormData[EditInvestorSectionNameEnum.EntityDetails],
      displayData: [
        { title: 'Investor Entity', value: legalEntityData?.entityName || '' },
        {
          title: 'Tax Status',
          value: taxStatus
        },
        {
          title: 'Entity Category',
          value: legalEntityData?.category ? Category[legalEntityData?.category] : ''
        },
        {
          title: 'Entity Type',
          value: legalEntityData?.entityType ? EntityType[legalEntityData?.entityType] : ''
        },
        {
          title: 'Entity Type Description',
          value: legalEntityData?.otherType || ''
        },
        {
          title: 'Eligibility',
          value: legalEntityData?.eligibility?.length ? (
            <>{legalEntityData?.eligibility.map((eligibility) => <p key={eligibility}>{Eligibility[eligibility]}</p>)}</>
          ) : null
        },
        {
          title: 'About Investor Notes',
          value: legalEntityData?.aboutInvestorNote || ''
        }
      ]
    },
    {
      title: 'Allocation Targets',
      name: EditInvestorSectionNameEnum.AllocationTargets,
      modalPageType: EditInvestorModalPageTypeEnum.AllocationTargets,
      formData: {
        capacityCalculationOption: ignoreAllocationTargets
          ? EditInvestorAllocationTargetsCapacityCalculationOptionEnum.DisregardAllocationTargets
          : targetOCRatio === null
            ? EditInvestorAllocationTargetsCapacityCalculationOptionEnum.PacingModel
            : EditInvestorAllocationTargetsCapacityCalculationOptionEnum.OverCommitmentRatio,
        assetClassTargetsOption: !!allocationTargets?.length
          ? EditInvestorAllocationTargetsAssetClassTargetsOptionEnum.CustomizeTargetsForThisInvestorEntity
          : EditInvestorAllocationTargetsAssetClassTargetsOptionEnum.UseDefaultAssetClassTargets,
        totalEntityValue: totalEntityValue ? totalEntityValue.toString() : '',
        overCommitRatio: targetOCRatio ? getDecimalPart(targetOCRatio).toString() : '',
        allocationTargets: buildAllocationTargets(assetClasses, allocationTargets, targetSubAssetClasses)
      } as IEditInvestorFormData[EditInvestorSectionNameEnum.AllocationTargets],
      displayData: [
        ...(entityType === InvestorDetailsEntityTypeEnum.GroupedEntity
          ? [
              {
                title: 'Total Entity Value',
                value: totalEntityValue ? (
                  <div>
                    <FormattingTooltip zIndex={1000}>{totalEntityValue}</FormattingTooltip>
                  </div>
                ) : (
                  '$0'
                )
              }
            ]
          : [
              ...(!ignoreAllocationTargets
                ? [
                    {
                      title: 'Capacity Calculation',
                      value:
                        targetOCRatio === null
                          ? EditInvestorAllocationTargetsCapacityCalculationOptionEnum.PacingModel
                          : EditInvestorAllocationTargetsCapacityCalculationOptionEnum.OverCommitmentRatio
                    },
                    {
                      title: 'Total Entity Value',
                      value: totalEntityValue ? (
                        <div>
                          <FormattingTooltip zIndex={1000}>{totalEntityValue}</FormattingTooltip>
                        </div>
                      ) : (
                        '$0'
                      )
                    },
                    { title: 'OC Ratio', value: targetOCRatio || '' }
                  ]
                : [{ title: 'Capacity Calculation', value: EditInvestorAllocationTargetsCapacityCalculationOptionEnum.DisregardAllocationTargets }])
            ])
      ]
    },
    {
      title: 'Allocation Settings',
      name: EditInvestorSectionNameEnum.AllocationSettings,
      modalPageType: EditInvestorModalPageTypeEnum.AllocationSettings,
      formData: {
        onHold,
        custodied,
        unmanaged,
        sri: legalEntityData?.sri || groupData?.impact || 'Not Specified',
        alwaysShow: alwaysShow
          ? EditInvestorAllocationSettingsAlwaysShowOptionEnum.ShowAll
          : EditInvestorAllocationSettingsAlwaysShowOptionEnum.ShowRecommended,
        biteSizeOption: ignoreBiteSize
          ? EditInvestorAllocationSettingsBiteSizeOptionEnum.UseMinimumRequiredCommitment
          : EditInvestorAllocationSettingsBiteSizeOptionEnum.SetSpecificDefaultBiteSize,
        sectorsToAvoid,
        defaultBiteSize1: defaultBiteSize1 ? defaultBiteSize1.toString() : '',
        defaultBiteSize2: defaultBiteSize2 ? defaultBiteSize2.toString() : ''
      } as IEditInvestorFormData[EditInvestorSectionNameEnum.AllocationSettings],
      displayData: [
        { title: 'On Hold', value: onHold ? EditInvestorAllocationSettingsOnHoldOptionEnum.Yes : '' },
        {
          title: 'Custodian Approval Required',
          value: custodied ? EditInvestorAllocationSettingsOnHoldOptionEnum.Yes : ''
        },
        {
          title: 'Investor Review',
          value: alwaysShow ? EditInvestorAllocationSettingsAlwaysShowOptionEnum.ShowAll : ''
        },
        {
          title: 'Default Amount for Initial Commitments',
          value: ignoreBiteSize
            ? EditInvestorAllocationSettingsBiteSizeOptionEnum.UseMinimumRequiredCommitment
            : EditInvestorAllocationSettingsBiteSizeOptionEnum.SetSpecificDefaultBiteSize
        },
        ...(!ignoreBiteSize
          ? [
              {
                title: 'Fund',
                value: defaultBiteSize1 ? (
                  <div>
                    <FormattingTooltip zIndex={1000}>{defaultBiteSize1}</FormattingTooltip>
                  </div>
                ) : (
                  '$0'
                )
              },
              {
                title: 'Direct',
                value: defaultBiteSize2 ? (
                  <div>
                    <FormattingTooltip zIndex={1000}>{defaultBiteSize2}</FormattingTooltip>
                  </div>
                ) : (
                  '$0'
                )
              }
            ]
          : []),
        {
          title: 'Sectors to Avoid',
          value: !!sectorsToAvoid.length ? (
            <>
              {sectorsToAvoid.map((sector) => (
                <p key={sector}>{sector}</p>
              ))}
            </>
          ) : null
        },
        {
          title: 'Unmanaged',
          value: unmanaged ? EditInvestorAllocationSettingsOnHoldOptionEnum.Yes : ''
        },
        {
          title: 'SRI',
          value: legalEntityData?.sri || groupData?.impact || 'Not Specified'
        }
      ]
    },
    {
      title: 'Data Integration',
      name: EditInvestorSectionNameEnum.DataIntegration,
      modalPageType: EditInvestorModalPageTypeEnum.DataIntegration,
      formData: {
        advisorCRMId: legalEntityData?.advisorCRMId || '',
        advisorReportingAccountNumber: legalEntityData?.advisorReportingAccountNumber || '',
        administratorCode: legalEntityData?.administratorCode || ''
      } as IEditInvestorFormData[EditInvestorSectionNameEnum.DataIntegration],
      isEmpty: !legalEntityData?.advisorCRMId && !legalEntityData?.advisorReportingAccountNumber && !legalEntityData?.administratorCode,
      displayData: [
        { title: 'Advisor CRM ID', value: legalEntityData?.advisorCRMId || '' },
        { title: 'Advisor Reporting Account Number', value: legalEntityData?.advisorReportingAccountNumber || '' },
        { title: 'Administrator Code', value: legalEntityData?.administratorCode || '' }
      ]
    }
  ];
};

export const buildAllocationTargets = (
  assetClasses: AssetClassesQuery['assetClasses'],
  allocationTargets: EditInvestorPageLegalEntityQuery['legalEntity']['allocationTargets'],
  targetSubAssetClasses: boolean
) => {
  const hasSubAssetClasses = assetClasses.some((assetClass) => assetClass.subAssetClasses.length > 0);

  if (!!allocationTargets.length) {
    return allocationTargets?.reduce((acc, curr) => {
      return {
        ...acc,
        [curr?.assetClass?.subAssetClass?.id || curr?.assetClass?.id]: `${curr.percentage.toFixed(2)}%`
      };
    }, {});
  }

  if (!allocationTargets.length && !hasSubAssetClasses && !targetSubAssetClasses) {
    return assetClasses?.reduce((acc, curr) => {
      return {
        ...acc,
        [curr?.id]: `${curr.defaultAllocationPercentage.toFixed(2)}%`
      };
    }, {});
  }

  if (!allocationTargets.length && hasSubAssetClasses && targetSubAssetClasses) {
    const subAssetClassTargets: Record<string, string> = {};

    assetClasses?.forEach((assetClass) => {
      assetClass.subAssetClasses?.forEach((subAssetClass) => {
        if (subAssetClass?.id) {
          subAssetClassTargets[subAssetClass.id] = `${subAssetClass.defaultAllocationPercentage.toFixed(2)}%`;
        }
      });
    });

    return subAssetClassTargets;
  }
};

export const buildAllocationTargetsToUpdate = (
  allocationTargets: Record<string, string>,
  assetClasses: AssetClassesQuery['assetClasses'],
  targetSubAssetClasses: boolean
) => {
  const hasSubAssetClasses = assetClasses.some((assetClass) => assetClass.subAssetClasses.length > 0);

  return Object.entries(allocationTargets)
    .filter(([_, percentage]) => percentage !== undefined)
    .map(([assetClassId, percentage]) => {
      if (targetSubAssetClasses && hasSubAssetClasses) {
        return {
          subAssetClassId: assetClassId,
          percentage: parseFloat(percentage || '0')
        };
      }
      return {
        assetClassId,
        percentage: parseFloat(percentage || '0')
      };
    });
};
